/// <amd-module name="Checks/ChecksOverview/ChecksPanel" />
import * as React from "react";

import { ChecksSource } from "Checks/ChecksOverview/ChecksSource";
import * as Resources from "Checks/ChecksOverview/Resources";
import { FeatureNames, publishTelemetry } from "Checks/ChecksOverview/Telemetry";
import { CheckAction, ICheckWrapperComponentProps } from "Checks/Client/Contracts/Contracts";
import * as PipelineChecksContracts from "Checks/Client/Contracts/PipelinesChecks";

import { ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";

import { TitleSize } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";

export interface ICheckPanelProps extends Partial<ICheckWrapperComponentProps> {
    contributionType: string;
    componentDependencies: string[];
    checkName: string;
    checkDefinitionRefId: string;
    isDisableChecksFeatureEnabled: boolean;
    resource: PipelineChecksContracts.Resource;
    defaultCheckSettings?: { [key: string] : string; };
}

export class ChecksPanel extends VssComponent<ICheckPanelProps, {}> {
    constructor(props: ICheckPanelProps, context: IVssComponentContext) {
        super(props, context);
        this._disableButtonObservable = new ObservableValue<boolean>(true);
        this._errorMessageObservable = new ObservableValue<string>("");
        this._pageContext = context.pageContext;
        this._source = ChecksSource.getInstance(this._pageContext); 
        if (this.props.isDisableChecksFeatureEnabled) {
            this._checkConfigurationData = Object.assign({}, this.props.checkConfigObject);
        }   
    }

    public render(): JSX.Element {
        return (
            <Observer isButtonDisabled={this._disableButtonObservable} errorMessage={this._errorMessageObservable}>
                {(observerProps: { isButtonDisabled: boolean, errorMessage: string }) => {
                    return (
                        <Panel
                            className="checks-panel"
                            onDismiss={this.props.onClosePanel ? this.props.onClosePanel : () => { }}
                            titleProps={{ text: this.props.checkName, size: TitleSize.Large }}
                            footerButtonProps={[
                                { text: Resources.ChecksPanelCancel, onClick: this.props.onClosePanel },
                                { text: this._getButtonText(), onClick: this._updateCheck, primary: true, disabled: observerProps.isButtonDisabled }
                            ]}>
                                <div className="checks-panel-content flex-column flex-grow">
                                    <WrappedComponent
                                        wrappedType={this.props.contributionType}
                                        wrappedProps={{
                                            ...this._getWrappedProps()
                                        }}
                                        dependencies={this.props.componentDependencies} />
                                    {!!observerProps.errorMessage &&
                                        <div className="panel-error-message-section" tabIndex={0}>
                                            {observerProps.errorMessage}
                                        </div>}
                                </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private _getWrappedProps = () => {
        let wrappedProps: ICheckWrapperComponentProps = {
            resource: this.props.resource,
            checkDefinition: this.props.checkDefinition || {},
            checkAction: this.props.checkAction,
            disableCreateButton: this._disableCreateButton,
            updateCheckDataAndEnableSave: this._updateCheckDataAndEnableSave,
            updateCheckDataAndDisableSave: this._updateCheckDataAndDisableSave,
            updateTelemetryData: this._updateTelemetryData,
            checkName: this.props.checkName,
            checkDefinitionRefId: this.props.checkDefinitionRefId,
            checkConfigObject: this.props.checkConfigObject,
            defaultCheckSettings: this.props.defaultCheckSettings
        };
        return wrappedProps;
    }

    private _disableCreateButton = (value: boolean) => {
        if (this._disableButtonObservable.value !== value) {
            this._disableButtonObservable.value = value;
        }
    }

    private _updateTelemetryData = (telemetryData: { [key: string]: string}) => {
        this._telemetryData = telemetryData;
    }

    private _publishTelemetryData = () => {
        let telemetryProperties: { [key: string]: string} = {
            ProjectAndResourceIdentifier: format("{0}:{1}:{2}", this._source._project, this.props.resource.type, this.props.resource.id),
            ...this._telemetryData
        };

        publishTelemetry(this._pageContext, this.props.checkAction === CheckAction.Create ? FeatureNames.AddCheck : FeatureNames.EditCheck, telemetryProperties);
    }

    private _updateCheckData = (record: PipelineChecksContracts.CheckConfiguration) => {
        if (this.props.isDisableChecksFeatureEnabled) {
            this._checkConfigurationData = Object.assign(this._checkConfigurationData, record);
        } else {
            this._checkConfigurationData = record;
        }
    }

    private _updateCheckDataAndDisableSave = (record: PipelineChecksContracts.CheckConfiguration) => {
        // Update state of button only if it is enabled, inorder to prevent unnecessary component re-rendering
        if (!this._disableButtonObservable.value) {
            this._disableButtonObservable.value = true;
        }
        this._updateCheckData(record);
    }

    private _updateCheckDataAndEnableSave = (record: PipelineChecksContracts.CheckConfiguration) => {
        // Update state of button only if it is disabled, inorder to prevent unnecessary component re-rendering
        if (this._disableButtonObservable.value) {
            this._disableButtonObservable.value = false;
        }
        this._updateCheckData(record);
    }

    private _updateCheck = () => {
        if (this.props.onUpdateCheck) {
            this.props.onUpdateCheck(this._checkConfigurationData, this.props.checkDefinitionRefId, this.props.checkName).then(() => {
                if (this.props.onClosePanel) {
                    this._publishTelemetryData();
                    this.props.onClosePanel();
                }
            }, (error) => {
                this._errorMessageObservable.value = error && error.message;
                if (error) {
                    announce(error.message);
                }
            });
        }
    }

    private _getButtonText = () => {
        return (this.props.checkAction === CheckAction.Create) ? Resources.ChecksPanelCreate : Resources.ChecksPanelSave;
    }

    private _disableButtonObservable: ObservableValue<boolean>;
    private _errorMessageObservable: ObservableValue<string>;
    private _checkConfigurationData: PipelineChecksContracts.CheckConfiguration;
    private _telemetryData: { [key: string]: string };
    private _source: ChecksSource;
    private _pageContext: IVssPageContext;
}
